import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { adminApi } from '../../lib/adminApi';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Upload, CalendarClock } from 'lucide-react';

const inputCls = 'bg-slate-950 border-white/10 text-white placeholder:text-slate-500 rounded-xl';
const pad = (n) => String(n).padStart(2, '0');
const toLocalInput = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

export const PostDialog = ({ post, onClose, onSave }) => {
  const [form, setForm] = useState(post);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    setForm(post && post.programado_para && !/Z|[+-]\d\d:\d\d$/.test(post.programado_para) ? { ...post, programado_para: `${post.programado_para}Z` } : post);
    setPreview(false);
  }, [post]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await adminApi.post('/uploads/image', fd);
      setForm((f) => ({ ...f, imagen_url: data.url }));
    } catch { /* toast handled by parent on save */ }
    finally { setUploading(false); e.target.value = ''; }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave(form); } catch { /* shown by parent */ } finally { setSaving(false); }
  };

  if (!form) return null;
  return (
    <Dialog open={!!post} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-white/10 text-white sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="post-dialog">
        <DialogHeader>
          <DialogTitle>{form.id ? 'Editar artículo' : 'Nuevo artículo'}</DialogTitle>
          <DialogDescription className="text-slate-400">Usa Markdown: ## Título, **negrita**, - lista, [enlace](https://...)</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid sm:grid-cols-[2fr_1fr_1fr] gap-3">
            <div><Label className="text-xs text-slate-300">Título</Label><Input data-testid="post-titulo-input" required minLength={3} maxLength={150} value={form.titulo} onChange={set('titulo')} className={inputCls} /></div>
            <div><Label className="text-xs text-slate-300">Autor</Label><Input data-testid="post-autor-input" maxLength={80} value={form.autor || ''} onChange={set('autor')} className={inputCls} /></div>
            <div><Label className="text-xs text-slate-300">Categoría</Label>
              <select data-testid="post-categoria-select" value={form.categoria || 'Consejos'} onChange={set('categoria')} className="w-full h-10 px-3 bg-slate-950 border border-white/10 text-white rounded-xl text-sm">
                {['Visas', 'Salud', 'Consejos', 'Destinos'].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div><Label className="text-xs text-slate-300">Resumen (aparece en Google y en las tarjetas)</Label><Textarea data-testid="post-resumen-input" required minLength={10} maxLength={400} rows={2} value={form.resumen} onChange={set('resumen')} className={inputCls} /></div>
          <div>
            <Label className="text-xs text-slate-300">Imagen de portada</Label>
            <div className="flex items-center gap-3 mt-1">
              {form.imagen_url && <img src={form.imagen_url} alt="" className="h-14 w-24 rounded-lg object-cover" data-testid="post-imagen-preview" />}
              <label className={`inline-flex items-center gap-2 cursor-pointer rounded-xl border border-white/20 px-4 py-2 text-sm hover:bg-white/10 ${uploading ? 'opacity-60 pointer-events-none' : ''}`}>
                <Upload className="h-4 w-4" /> {uploading ? 'Subiendo...' : 'Subir imagen'}
                <input type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="post-imagen-file" />
              </label>
              <Input data-testid="post-imagen-input" maxLength={500} value={form.imagen_url || ''} onChange={set('imagen_url')} className={`${inputCls} h-9 text-xs flex-1`} placeholder="o pega una URL" />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs text-slate-300">Contenido</Label>
              <button type="button" onClick={() => setPreview((v) => !v)} className="text-xs text-blue-300 hover:text-blue-200" data-testid="post-preview-toggle">{preview ? 'Editar' : 'Vista previa'}</button>
            </div>
            {preview ? (
              <div className="prose prose-invert prose-sm max-w-none bg-slate-950 rounded-xl p-4 min-h-[200px]" data-testid="post-preview"><ReactMarkdown remarkPlugins={[remarkGfm]}>{form.contenido || '_Sin contenido_'}</ReactMarkdown></div>
            ) : (
              <Textarea data-testid="post-contenido-input" required minLength={20} maxLength={20000} rows={12} value={form.contenido} onChange={set('contenido')} className={`${inputCls} font-mono text-sm`} />
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-3 items-end">
            <div><Label className="text-xs text-slate-300">URL personalizada (opcional)</Label><Input data-testid="post-slug-input" maxLength={80} value={form.slug || ''} onChange={set('slug')} className={inputCls} placeholder="se genera del título" /></div>
            <label className="flex items-center gap-2 text-sm text-slate-300 h-10"><input type="checkbox" checked={!!form.publicado} onChange={(e) => setForm((f) => ({ ...f, publicado: e.target.checked }))} data-testid="post-publicado-checkbox" /> Publicado (visible en el blog)</label>
          </div>
          {!form.publicado && (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 space-y-2" data-testid="post-schedule-box">
              <Label className="text-xs text-amber-100 flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> Programar publicación automática (hora de tu dispositivo)</Label>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input type="datetime-local" data-testid="post-programado-input" min={toLocalInput(new Date(Date.now() + 5 * 60000))} value={form.programado_para ? toLocalInput(new Date(form.programado_para)) : ''} onChange={(e) => setForm((f) => ({ ...f, programado_para: e.target.value ? new Date(e.target.value).toISOString() : null }))} className={`${inputCls} h-10 sm:max-w-xs`} />
                {form.programado_para && <Button type="button" variant="ghost" onClick={() => setForm((f) => ({ ...f, programado_para: null }))} data-testid="post-programado-clear" className="h-10 rounded-xl text-amber-200 hover:bg-white/10 hover:text-white">Quitar programación</Button>}
              </div>
              <p className="text-xs text-amber-200/80">{form.programado_para ? `Se publicará automáticamente el ${new Date(form.programado_para).toLocaleString('es-VE')} y avisará a los suscriptores si la opción de abajo está marcada.` : 'Déjalo vacío para guardarlo como borrador sin fecha.'}</p>
            </div>
          )}
          <label className={`flex items-start gap-2 text-sm rounded-xl border p-3 ${form.notificado_at ? 'border-white/10 text-slate-500' : 'border-teal-500/30 bg-teal-500/10 text-teal-100'}`}>
            <input type="checkbox" disabled={!!form.notificado_at} checked={form.notificado_at ? false : form.notificar !== false} onChange={(e) => setForm((f) => ({ ...f, notificar: e.target.checked }))} data-testid="post-notificar-checkbox" className="mt-0.5" />
            <span>{form.notificado_at ? `Ya se avisó a ${form.notificados ?? ''} suscriptores por email.` : 'Avisar por email a todos los suscriptores activos cuando se publique este artículo.'}</span>
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-xl border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white">Cancelar</Button>
            <Button type="submit" disabled={saving || uploading} className="rounded-xl bg-blue-600 hover:bg-blue-500" data-testid="post-save-btn">{saving ? 'Guardando...' : 'Guardar'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PostDialog;
